import { cva } from 'class-variance-authority';

/**
 * Multi-line text input class recipes.
 *
 * Like `Button.recipe` and `TextInput.recipe`, these contain ZERO `dark:` variants — colour comes
 * entirely from the `--ds-textarea-*` slots that `.ds-textarea` sets in
 * `components/styles/design-system.css`. Unlike `TextInput.recipe`, the class behind those slots
 * needs no dark rule at all: every one of them names a semantic role, and a role already carries a
 * value per theme.
 *
 * Every state selector reads Base UI's `Field.Root` attributes through the `group/ti` marker
 * (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`), so there is no React state
 * behind the shrink or the colour changes — which also means the states are reachable from a story
 * or a test by rendering, not by simulating.
 *
 * The state selectors are MUTUALLY EXCLUSIVE rather than relying on utility sort order, the same
 * discipline the button and text input recipes use. Precedence, highest first:
 *
 *   disabled → invalid → focused → hover → resting
 *
 * An errored field therefore stays red while focused, which is what the design's separate Error and
 * Focus cells imply.
 *
 * ── Why none of this imports `TextInput.recipe` ────────────────────────────────────────────────
 *
 * The geometry below is a near-copy of `textInputBoxRecipe`'s, and importing it would be less code.
 * It is restated anyway, for two reasons that pull the same way.
 *
 * The first is the namespace. `textInputRootRecipe()` emits the literal string `ds-input`, which is
 * the single token that would drag the first-generation palette basis back in — this component
 * resolves from roles, that one resolves from `--color-*` steps, and there is no partial version of
 * that. `textInputBoxRecipe` is left alone for the independence reason `.ds-textarea`'s own comment
 * gives: a change made for one field must not be able to break the other.
 *
 * The second is that the duplication is GATED rather than trusted. `Select` reuses the recipe and
 * calls the result drift-proof "by construction rather than by matching values that could later
 * diverge"; dropping the reuse drops that guarantee, so it comes back as an assertion —
 * `TextArea.test.tsx` imports both recipes and compares the radius, border width and min-height
 * they emit for every `position`. That test is load-bearing, not decorative: it is the only thing
 * standing between these two files and a silent one-pixel disagreement, because the slot harness
 * cannot see a Tailwind literal in a TypeScript file.
 *
 * ── Where the padding lives ────────────────────────────────────────────────────────────────────
 *
 * All of it is on the CONTROL, none on the box, exactly as on `TextInput` and for exactly the same
 * reason: padding the box would leave the `<textarea>` a strip inside a larger box, so clicking
 * near the edges would do nothing and the caret would land in the wrong place. Paying the padding
 * from inside the control makes the element fill the box, so every pixel of the field is a hit
 * target.
 *
 * It matters MORE here than it does next door. A single-line field's dead zone is at worst the
 * 20px above and below one line of text; a five-row field's would be the whole left and right
 * gutter down its entire height, which is where a user reaches to place a caret mid-paragraph.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-textarea` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders as a SIBLING of the box would otherwise fall outside their scope
 * and resolve to inherited colour instead of failing visibly.
 *
 * The group marker is `group/ti` rather than a name of its own. It is shared with `TextInput` on
 * purpose — the two are alternative fields, never nested one inside the other, so there is no
 * ambiguity to resolve — and sharing it means a stacked group's state selectors read the same
 * whichever field type is in it.
 */
export const textAreaRootRecipe = cva(['group/ti ds-textarea', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own (see the note above) — it is a border, a fill, a radius and two focus
 * affordances. The only exception is the bottom inset when helper text is present, which the
 * control can no longer pay for because the helper sits below it.
 *
 * `gap-[4px]` separates the value block from the helper text. The label is absolutely positioned
 * and out of flow, so the gap only ever applies between the control and the helper row — a field
 * without helper text gets no stray space.
 *
 * ── Why `min-h-[100px]`, which is deliberately NOT `TextInput`'s floor ────────────────────────
 *
 * Read off the design (node 1020-25860): all thirty cells are 300×100, authored as a fixed 100px
 * height with a matching 100px floor and a scrollbar drawn inside. The drawn field does not grow
 * with its content — it is a 100px box that scrolls.
 *
 * `rows` stays the height mechanism here, because a multi-line field whose size the caller cannot
 * choose is the more surprising shape in a form, and the default of three rows already lands at
 * roughly the 100px the design draws. What the design settles is the FLOOR: a `rows={1}` field
 * should still occupy the box the design specifies rather than collapsing to one line.
 *
 * An earlier revision used 60px — `TextInput`'s resting height — reasoning that a shorter field
 * beside a text input would read as a broken row in a stacked group. That was a fair guess while
 * the design file was unreachable, and it was wrong. The stacked-group contract is about the SEAM
 * between fields rather than their heights: the group collapses adjacent borders, and it is the
 * corner radii that make that seam read as one line. Those are still pinned to `TextInput`'s.
 *
 * ── Why the stroke is drawn with shadows, not the border ───────────────────────────────────────
 *
 * `border-width` NEVER changes here, in any state, and the reason is the one `textInputBoxRecipe`
 * spells out: a border is layout, so thickening it on focus moves the padding box and shifts the
 * absolutely positioned label and the value text by half a pixel. On a multi-line field that jump
 * is more visible, not less — every line of a filled paragraph moves at once.
 *
 * So the border stays at its resting 1px and everything state-dependent is painted as an inset
 * `box-shadow`, which occupies no layout space:
 *
 *   inset 0 0 0 0.5px  --ds-textarea-stroke-extra   the half-pixel that makes the stroke 1.5px
 *   inset 0 0 0 2.5px  --ds-textarea-ring-current   the lighter tint, in the 2px inside that
 *
 * Earlier shadows paint over later ones, so reading inward that is 1px of border, 0.5px of the same
 * colour, then 2px of tint. Both slots default to `transparent`, which is what lets the shadow rule
 * be unconditional — only the slots are switched per state, by the same mutually exclusive
 * selectors that set the border colour.
 *
 * ── Two focus affordances, which is a question for the design review ───────────────────────────
 *
 * A focused field draws the inset tint above AND a 2px outline 2px outside the box. They are not
 * redundant in role: the tint is a low-contrast wash that would not satisfy 1.4.11 at any
 * proportion that still read as a halo, and the outline is the indicator that actually clears 3:1.
 * The tint is inherited from the field shape next door; the outline is Natter's standard focus
 * treatment, the same one `Button.recipe` draws, and it is what the Text Area design binds.
 *
 * Keeping both is the conservative call — dropping the outline would put this component below the
 * repo's focus bar, and dropping the tint would make it the only field that looks different when
 * focused. Whether a field needs both is a finish question rather than a correctness one, and it
 * is on the design review's agenda.
 *
 * The outline is keyed off `has-[:focus-visible]` rather than written with Button's
 * `focus-visible:` prefix, because the element carrying it is a `<div>` and cannot be focused. The
 * distinction is not cosmetic: `group-data-[focused]` — which is what the rest of this recipe
 * reads — is set by Base UI for ANY focus, so using it here would draw a 2px outline on every mouse
 * click into the field. `has-[:focus-visible]` asks the browser the same question Button's prefix
 * asks, one element up. (For a textarea the browser answers yes on a pointer click too, which is
 * the spec'd behaviour for a text entry control and is intended.)
 */
export const textAreaBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'min-h-[100px]',
    'border-solid border-[length:var(--ds-textarea-border-width)] border-[var(--ds-textarea-border)]',
    'bg-[var(--ds-textarea-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow,outline-color] duration-150 motion-reduce:transition-none',

    // Hover is excluded from every other state so it can never fight them.
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-textarea-border-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-textarea-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-textarea-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-textarea-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-textarea-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Both shadow layers off by default, so the rule below can be unconditional.
    '[--ds-textarea-stroke-extra:transparent] [--ds-textarea-ring-current:transparent]',
    'shadow-[inset_0_0_0_0.5px_var(--ds-textarea-stroke-extra),inset_0_0_0_2.5px_var(--ds-textarea-ring-current)]',

    // The half-pixel thickening, in the same colour as whichever border is showing. Focus, error
    // and disabled each reach 1.5px; hover and resting stay at 1px.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-textarea-stroke-extra:var(--ds-textarea-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:[--ds-textarea-stroke-extra:var(--ds-textarea-border-invalid)]',
    'group-data-[disabled]/ti:[--ds-textarea-stroke-extra:var(--ds-textarea-border-disabled)]',

    /**
     * The tint, drawn only while focused — inside the border, flush against it.
     *
     * An errored field keeps its red border when focused, so a blue tint inside it would read as a
     * bug. Remapping the slot rather than adding a focused-and-invalid rule keeps the line below a
     * single read, exactly as the border rules are.
     */
    'group-data-[invalid]/ti:[--ds-textarea-ring:var(--ds-textarea-ring-invalid)]',
    'group-data-[focused]/ti:[--ds-textarea-ring-current:var(--ds-textarea-ring)]',

    /**
     * The focus indicator proper — see the long note above for why it is `has-[:focus-visible]`.
     *
     * It names `--border-focus` directly rather than through a slot, which is what `Button.recipe`
     * does with the same treatment: the outline is not part of the component's palette, it is the
     * one focus colour the whole system shares, and routing it through a private slot would imply
     * a per-component decision that does not exist.
     *
     * `outline-transparent` on the base so the colour has something to transition FROM; without it
     * the outline appears at full strength on the first frame.
     */
    'outline-transparent',
    'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[var(--border-focus)]',
  ],
  {
    variants: {
      /**
       * Figma's `Corner Radius` axis. The design draws three of these four; `middle` has no cell
       * and is extrapolated — see `TextInputPosition`, which this component imports rather than
       * re-declaring.
       */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
      },
      /**
       * The bottom inset, which moves between the box and the control depending on whether anything
       * is rendered below the value. A plain 10px: the border never changes width, so nothing here
       * has to compensate for it.
       */
      hasHelper: {
        true: 'pb-[10px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasHelper: false },
  }
);

/**
 * The floating label.
 *
 * Mechanically identical to `textInputLabelRecipe` — same 19px resting offset, same 10px shrunk
 * offset, same 11/14 scale, same composited `translate`/`scale` animation — so a `TextArea` and a
 * `TextInput` stacked in one group have their labels on exactly the same lines. That alignment is
 * the reason to copy the numbers rather than re-derive them.
 *
 * One thing the shared numbers MEAN differently here, and it is worth naming. On a 60px field, 19px
 * centres the resting label in the box; `textInputLabelRecipe` describes it that way. On a field
 * that is 100px at three rows and taller at five, it centres nothing — it pins the label to the top,
 * above the first line of the value. That is the only reading that makes sense for a multi-line
 * field, where the value grows downwards and a centred label would drift away from the text it
 * names, so the value is right for a reason that is not the one next door gives for it.
 *
 * Deliberately NOT `pointer-events-none`. This is a real `<label>` wired to the control by `Field`,
 * so a click on it focuses the textarea natively; making it click-through would instead drop
 * clicks on the part of it that overhangs the control.
 */
export const textAreaLabelRecipe = cva([
  'absolute top-0 left-[24px] origin-left cursor-text select-none',
  'font-sans text-body-md text-[var(--ds-textarea-label)]',
  'max-w-[calc(100%_-_48px)] truncate',
  'translate-y-[19px] scale-100',

  /**
   * Rise and shrink are ONE composited animation.
   *
   * The properties named are `translate` and `scale`, not `transform`: Tailwind v4's `translate-*`
   * and `scale-*` utilities set the standalone CSS properties of those names, so a transition on
   * `transform` would match nothing and the label would jump to its shrunk position before
   * animating its size. `scale-100` is stated on the base rather than left implicit, because
   * interpolating from `scale: none` is not reliably animatable.
   */
  'transition-[translate,scale,color] duration-150 motion-reduce:transition-none',

  // 11/14 — the shrunk size as a ratio of the resting one, since the font-size itself never changes.
  'group-data-[filled]/ti:translate-y-[10px] group-data-[filled]/ti:scale-[0.785714]',
  'group-data-[focused]/ti:translate-y-[10px] group-data-[focused]/ti:scale-[0.785714]',

  /**
   * And whenever a placeholder is showing, which is the third way the value line gets occupied.
   *
   * Without this the resting label sits exactly where the placeholder draws and the two overlap
   * into an unreadable stack — the label is centred on the value line when the field is empty, and
   * a placeholder is by definition only visible when the field is empty. The design's answer is
   * that the placeholder takes the value's place, in `foreground/secondary`, which only works if
   * the label has already moved out of the way.
   *
   * `:placeholder-shown` is the exact condition and nothing narrower is needed: it matches only
   * when the element HAS a placeholder and is empty, so a field without one never matches and its
   * label still rests centred. Reading it off the control through `group-has-` rather than adding
   * a prop keeps the component's state observed from the DOM, like every other state here.
   */
  'group-has-[:placeholder-shown]/ti:translate-y-[10px] group-has-[:placeholder-shown]/ti:scale-[0.785714]',
  'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-textarea-label-disabled)]',
]);

/**
 * The `<textarea>`, which is also the field's entire hit area.
 *
 * `pt-[28px]` puts the first line of the value on the same line `TextInput` draws its value on, so
 * the two agree when stacked. Everything above that line is still part of the control rather than
 * dead space on the box.
 *
 * ── The resets ─────────────────────────────────────────────────────────────────────────────────
 *
 * Preflight is deliberately absent from the token layer, so the browser's own control styling is
 * still in play and every reset is spelled out rather than assumed. `<textarea>` needs two that
 * `<input>` does not: `align-top`, because a replaced inline-block element sits on the text
 * baseline by default and leaves a few pixels of descender space under the box, and an explicit
 * `resize` (below).
 *
 * ── Why vertical-only resize ───────────────────────────────────────────────────────────────────
 *
 * `resize-y` rather than the browser's `both` default, and rather than `resize-none`.
 *
 * Horizontal resize has to go: the control is `w-full` inside a bordered box, so widening it pushes
 * the text out through the right border — the box does not grow with it, and in a stacked group the
 * result is one field wider than its neighbours with a seam down the middle of the stack.
 *
 * Vertical resize stays. Removing it entirely is the tidier-looking option and is what most design
 * systems reach for, but "this box is too small for what I am writing" is the single most common
 * complaint about a multi-line field, and the grabber is the only affordance that answers it
 * without the component owning an autosize behaviour. Nothing depends on the height: the box is
 * `flex-col` and grows with the control, and the stacked group collapses borders vertically, so a
 * resized field keeps its seams.
 */
const controlBase = [
  'm-0 block w-full appearance-none border-0 bg-transparent align-top',
  'font-sans text-body-md text-[var(--ds-textarea-value)]',
  'outline-none',
  'resize-y',
  'px-[24px] pt-[28px]',
  'cursor-text',
  'placeholder:text-[var(--ds-textarea-placeholder)]',
  'disabled:cursor-not-allowed disabled:resize-none disabled:text-[var(--ds-textarea-value-disabled)]',
];

export const textAreaControlRecipe = cva(controlBase, {
  variants: {
    /** When helper text follows, the bottom inset moves to the box — see `textAreaBoxRecipe`. */
    hasHelper: {
      true: 'pb-0',
      false: 'pb-[10px]',
    },
  },
  defaultVariants: { hasHelper: false },
});

/**
 * Helper / error text, rendered inside the border as `TextInput`'s 60px variant does.
 *
 * Inside rather than below, and that placement is what makes the stacked group work: `TextInputGroup`
 * collapses the borders of adjacent CHILDREN, so anything rendered outside the box would sit between
 * two borders that are supposed to be one line. A field whose helper text broke the seam could not
 * satisfy the group contract at all.
 */
export const textAreaHelperRecipe = cva([
  'flex items-center',
  // `m-0` is load-bearing rather than tidiness: `Field.Description` renders a `<p>`, and with
  // Preflight absent it keeps the browser's default `margin: 1em 0` — 28px of stray space that the
  // `<div>` `Field.Error` renders does not have, so the box would change height with the state.
  'm-0',
  // Aligned with the value text, which pays its own 24px gutter from inside the control. The
  // size comes from `Body/sm`, the scale's own helper-and-caption style.
  'px-[24px]',
  'font-sans text-body-sm text-[var(--ds-textarea-helper)]',
  'group-data-[invalid]/ti:text-[var(--ds-textarea-helper-invalid)]',
]);
